import React, { ReactNode, useMemo, createContext, useRef } from 'react';
import {
  GoogleMap as GoogleMapComponent,
  Libraries,
  LoadScriptNext,
} from '@react-google-maps/api';

import { mapOptions } from './googleMaps.utils';

interface GoogleMapProps {
  center?: google.maps.LatLngLiteral;
  children?: ReactNode;
  containerStyle?: React.CSSProperties;
  defaultCenter?: google.maps.LatLngLiteral;
  defaultMapOptions?: google.maps.MapOptions;
  defaultZoom?: number;
  libraries?: Libraries;
}

interface MapContextProps {
  defaultCenter: google.maps.LatLngLiteral;
  defaultZoom: number;
  mapRef: React.MutableRefObject<google.maps.Map | undefined>;
}

export const MapContext = createContext<MapContextProps | null>(null);
const { Provider } = MapContext;

const defaultContainerStyle: React.CSSProperties = {
  height: '400px',
  width: '400px',
};

const defaultCenter: google.maps.LatLngLiteral = {
  lat: -3.745,
  lng: -38.523,
};

const GoogleMap: React.FC<GoogleMapProps> = ({
  center = defaultCenter,
  defaultZoom = 14,
  containerStyle = defaultContainerStyle,
  defaultMapOptions = mapOptions,
  children,
  libraries = ['drawing'],
}) => {
  const mapRef = useRef<google.maps.Map | undefined>();

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const mapContainerStyle = useMemo(
    () => ({
      ...defaultContainerStyle,
      ...containerStyle,
    }),
    [containerStyle]
  );

  const mapContextValue: MapContextProps = useMemo(
    () => ({
      defaultCenter: center,
      defaultZoom,
      mapRef,
    }),
    [center, defaultZoom]
  );

  if (!apiKey) {
    return (
      <div className="text-danger fw-bold">
        Google Maps API key not found. Please check your environment variables.
      </div>
    );
  }

  return (
    <Provider value={mapContextValue}>
      <LoadScriptNext googleMapsApiKey={apiKey} libraries={libraries}>
        <GoogleMapComponent
          data-testid="map-container"
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={defaultZoom}
          options={defaultMapOptions}
          onLoad={map => {
            mapRef.current = map;
          }}
        >
          {children}
        </GoogleMapComponent>
      </LoadScriptNext>
    </Provider>
  );
};

export default React.memo(GoogleMap);
